import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck,
  HeartPulse,
  Cross,
  AlertTriangle,
  CheckCircle2,
  ArrowLeft,
  Info,
} from "lucide-react";
import { fetchPolicies } from "../lib/api";
import { useProfile } from "../context/ProfileContext";
import { representativeAnnualIncome } from "../lib/profileOptions";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { CurrencyHint } from "../components/CurrencyHint";
import { formatINRCompact } from "../lib/currency";
import { Disclaimer } from "../components/Disclaimer";

/**
 * Sum-Assured Coverage-Gap Analyzer.
 *
 * Compares the total sum-assured the member holds (grouped by risk category) against the
 * industry-norm recommendation derived from their profile. Reads live policies from
 * `GET /api/policies` (no new backend endpoint — this is a pure aggregation view).
 *
 * Buckets:
 *   - Life cover     = sum of SA across (term_life + life_insurance) policies
 *                      Recommendation: annualIncome × HLV_MULTIPLIER (default 15) — the classic
 *                      human-life-value approach recommended by IRDAI advisories.
 *   - Accident cover = sum of SA across accident policies
 *                      Recommendation: annualIncome × ACCIDENT_MULTIPLIER (default 5).
 *   - Health cover   = sum of SA across health policies
 *                      Recommendation: fixed floor per family-size bucket (single vs family), as
 *                      health-cover need is expense-based, not income-based.
 *
 * User can adjust the multipliers/floor inline — the page recomputes on every keystroke.
 * No persistence: this is a one-shot diagnostic view, matching the same MVP philosophy as the
 * Lean FIRE and LIC calculators shipped in Session 1.
 */

const LIFE_COVER_TYPES = new Set(["term_life", "life_insurance"]);
const ACCIDENT_TYPES = new Set(["accident"]);
const HEALTH_TYPES = new Set(["health"]);

// Default recommendation parameters — IRDAI + industry-consensus benchmarks (Feb 2026).
const DEFAULTS = {
  hlvMultiplier: 15,       // life cover = 15× annual income
  accidentMultiplier: 5,   // accident cover = 5× annual income
};

// Family-composition options. `range` is what we surface to the user; `floor` is the low end
// and `ceiling` the high end used in the tier matrix below.
const FAMILY_OPTIONS = [
  {
    value: "single",
    label: "Single adult",
    rangeLabel: "₹5L – ₹15L",
    floor: 5_00_000,
    ceiling: 15_00_000,
  },
  {
    value: "couple",
    label: "Couple",
    rangeLabel: "₹10L – ₹25L",
    floor: 10_00_000,
    ceiling: 25_00_000,
  },
  {
    value: "couple_1_child",
    label: "Couple + 1 Child",
    rangeLabel: "₹25L – ₹50L",
    floor: 25_00_000,
    ceiling: 50_00_000,
  },
  {
    value: "couple_2_children",
    label: "Couple + 2 Children",
    rangeLabel: "₹50L – ₹1 Cr",
    floor: 50_00_000,
    ceiling: 1_00_00_000,
  },
  {
    value: "family_with_seniors",
    label: "Family with Senior Parents",
    rangeLabel: "₹1 Cr+",
    floor: 1_00_00_000,
    ceiling: 1_50_00_000,
    note: "Seniors are best covered on a separate senior-citizen policy — a single floater may be denied or heavily loaded.",
  },
];

// City-tier options. Tier 1 uses the ceiling (metro treatment costs), Tier 3 the floor,
// Tier 2 the midpoint. The `factor` is applied against the family-composition range.
const CITY_TIER_OPTIONS = [
  { value: "tier1", label: "Tier 1 (Metro)", description: "Mumbai / Delhi / Bengaluru etc.", weight: 1 },
  { value: "tier2", label: "Tier 2 (Emerging)", description: "Pune / Jaipur / Kochi etc.", weight: 0.5 },
  { value: "tier3", label: "Tier 3 / Rest of India", description: "Smaller cities & towns", weight: 0 },
];

// Age brackets → multiplier applied to the family × tier baseline. Insurers price health
// premiums by age bands; the recommended cover follows the same intuition because
// hospitalisation risk (and per-day treatment cost for that age group) rises with age.
const AGE_BRACKETS = [
  { max: 30, factor: 0.8, label: "under 30" },
  { max: 40, factor: 1.0, label: "30–40" },
  { max: 50, factor: 1.2, label: "40–50" },
  { max: 60, factor: 1.4, label: "50–60" },
  { max: Infinity, factor: 1.6, label: "60+" },
];

function ageFactorFor(age) {
  const a = Number(age);
  if (!a || a <= 0) return { factor: 1.0, label: "no age set (using baseline)" };
  const bracket = AGE_BRACKETS.find((b) => a < b.max) || AGE_BRACKETS[AGE_BRACKETS.length - 1];
  return { factor: bracket.factor, label: `age ${bracket.label}` };
}

function recommendedHealthFloor(familyValue, tierValue, age) {
  const fam = FAMILY_OPTIONS.find((f) => f.value === familyValue) || FAMILY_OPTIONS[0];
  const tier = CITY_TIER_OPTIONS.find((t) => t.value === tierValue) || CITY_TIER_OPTIONS[0];
  const { factor } = ageFactorFor(age);
  // Linear interpolation between the family's floor and ceiling using the tier weight,
  // then scaled by the age factor.
  const baseline = fam.floor + (fam.ceiling - fam.floor) * tier.weight;
  return Math.round(baseline * factor);
}

function sumSA(policies, typeSet) {
  // The policies API returns each policy with a `type` field (see backend `PolicyOut`).
  // Filtering on `policy_type` never matched, so every "Current cover" totalled to 0 and
  // the analyzer ignored existing cover entirely. Bug fixed 11-Feb-2026.
  return policies
    .filter((p) => typeSet.has(p.type))
    .reduce((acc, p) => acc + (Number(p.sum_assured) || 0), 0);
}

function gapMeta(current, recommended) {
  if (recommended <= 0) return { pct: 100, band: "grey", status: "n/a" };
  const pct = Math.min(Math.round((current / recommended) * 100), 100);
  if (pct >= 100) return { pct, band: "green", status: "adequate" };
  if (pct >= 60) return { pct, band: "amber", status: "under-insured" };
  return { pct, band: "red", status: "severely under-insured" };
}

function BandPill({ band, status }) {
  const styles = {
    green: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
    amber: "border-amber-400/40 bg-amber-400/10 text-amber-300",
    red: "border-red-400/40 bg-red-400/10 text-red-300",
    grey: "border-border bg-elevated text-muted",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${styles[band] || styles.grey}`}
      data-testid={`gap-status-${band}`}
    >
      {band === "green" ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
      {status}
    </span>
  );
}

function GapBar({ pct, band }) {
  const barStyles = {
    green: "bg-emerald-400",
    amber: "bg-amber-400",
    red: "bg-red-400",
    grey: "bg-muted",
  };
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-elevated" data-testid="gap-bar">
      <div
        className={`h-full transition-all ${barStyles[band] || barStyles.grey}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function CoverageCard({ icon: Icon, title, description, current, recommended, testIdSuffix }) {
  const meta = gapMeta(current, recommended);
  const gap = Math.max(recommended - current, 0);
  return (
    <Card data-testid={`coverage-card-${testIdSuffix}`}>
      <CardHeader className="flex-row items-center gap-3 space-y-0 pb-3">
        <div className="rounded-md bg-elevated p-2 text-gold">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <CardTitle className="text-base">{title}</CardTitle>
          <p className="text-xs text-muted">{description}</p>
        </div>
        <BandPill band={meta.band} status={meta.status} />
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted">Current cover</p>
            <p className="text-lg font-semibold text-text" data-testid={`current-${testIdSuffix}`}>
              {formatINRCompact(current)}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted">Recommended</p>
            <p className="text-lg font-semibold text-gold" data-testid={`recommended-${testIdSuffix}`}>
              {formatINRCompact(recommended)}
            </p>
          </div>
        </div>
        <GapBar pct={meta.pct} band={meta.band} />
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted">Coverage {meta.pct}% of recommendation</span>
          {gap > 0 && (
            <span className="font-medium text-text" data-testid={`gap-${testIdSuffix}`}>
              Gap: {formatINRCompact(gap)}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function CoverageGapPage() {
  const { profile } = useProfile();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);

  // Recommendation parameters — user can override any of them to model scenarios.
  const [annualIncome, setAnnualIncome] = useState(0);
  const [familyComposition, setFamilyComposition] = useState("couple_1_child");
  const [cityTier, setCityTier] = useState("tier1");
  const [age, setAge] = useState("");
  const [hlvMultiplier, setHlvMultiplier] = useState(DEFAULTS.hlvMultiplier);
  const [accidentMultiplier, setAccidentMultiplier] = useState(DEFAULTS.accidentMultiplier);

  useEffect(() => {
    fetchPolicies()
      .then(setPolicies)
      .catch((err) => toast.error(err.response?.data?.detail || "Could not load policies."))
      .finally(() => setLoading(false));
  }, []);

  // Once the profile loads, pre-fill annual income from the income-range bucket.
  useEffect(() => {
    if (profile?.income_range) {
      const derived = representativeAnnualIncome(profile.income_range);
      if (derived) setAnnualIncome(derived);
    }
  }, [profile]);

  const totals = useMemo(() => {
    const life = sumSA(policies, LIFE_COVER_TYPES);
    const accident = sumSA(policies, ACCIDENT_TYPES);
    const health = sumSA(policies, HEALTH_TYPES);
    const income = Number(annualIncome) || 0;
    return {
      life: {
        current: life,
        recommended: income * (Number(hlvMultiplier) || 0),
      },
      accident: {
        current: accident,
        recommended: income * (Number(accidentMultiplier) || 0),
      },
      health: {
        current: health,
        recommended: recommendedHealthFloor(familyComposition, cityTier, age),
      },
    };
  }, [policies, annualIncome, hlvMultiplier, accidentMultiplier, familyComposition, cityTier, age]);

  const selectedFamily = FAMILY_OPTIONS.find((f) => f.value === familyComposition);
  const selectedTier = CITY_TIER_OPTIONS.find((t) => t.value === cityTier);
  const ageInfo = ageFactorFor(age);

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="rounded-lg border border-border bg-card p-10 text-center text-muted">
          Loading your policies…
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8" data-testid="coverage-gap-page">
      <div className="mb-6 flex items-center gap-2 text-xs text-muted">
        <Link to="/app/policies" className="flex items-center gap-1 hover:text-gold" data-testid="back-to-policies">
          <ArrowLeft className="h-3 w-3" />
          Back to policies
        </Link>
      </div>

      <div className="mb-8">
        <p className="text-xs uppercase tracking-widest text-gold">Insurance</p>
        <h1 className="mt-1 text-3xl font-semibold text-text">Coverage Gap Analyzer</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted">
          Compare the cover you actually have with the industry-norm recommendation for your income and
          family size. Adjust the multipliers below to model different assumptions — nothing is saved.
        </p>
      </div>

      {/* Recommendation inputs */}
      <Card className="mb-8" data-testid="coverage-inputs-card">
        <CardHeader>
          <CardTitle className="text-base">Your inputs</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cg-income">Annual income</Label>
            <Input
              id="cg-income"
              type="number"
              value={annualIncome || ""}
              onChange={(e) => setAnnualIncome(e.target.value)}
              data-testid="input-annual-income"
            />
            <CurrencyHint value={annualIncome} />
            {profile?.income_range && (
              <p className="text-xs text-muted">Pre-filled from your saved profile.</p>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cg-family">Family composition</Label>
            <select
              id="cg-family"
              value={familyComposition}
              onChange={(e) => setFamilyComposition(e.target.value)}
              className="h-10 rounded-md border border-border bg-elevated px-3 text-sm text-text focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold"
              data-testid="input-family-composition"
            >
              {FAMILY_OPTIONS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label} · {f.rangeLabel}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted">Sets the health-cover range.</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cg-tier">Location</Label>
            <select
              id="cg-tier"
              value={cityTier}
              onChange={(e) => setCityTier(e.target.value)}
              className="h-10 rounded-md border border-border bg-elevated px-3 text-sm text-text focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold"
              data-testid="input-city-tier"
            >
              {CITY_TIER_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted">
              {selectedTier?.description}. Tier 1 uses the top of the range, Tier 3 the floor.
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cg-age">Age (oldest primary insured)</Label>
            <Input
              id="cg-age"
              type="number"
              min="18"
              max="99"
              placeholder="e.g. 35"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              data-testid="input-age"
            />
            <p className="text-xs text-muted">
              Age band × {ageInfo.factor.toFixed(1)} multiplier ({ageInfo.label}). Older
              primary insured → higher recommended cover.
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cg-hlv">Life cover multiplier (× income)</Label>
            <Input
              id="cg-hlv"
              type="number"
              min="1"
              max="30"
              value={hlvMultiplier}
              onChange={(e) => setHlvMultiplier(e.target.value)}
              data-testid="input-hlv-multiplier"
            />
            <p className="text-xs text-muted">IRDAI norm: 10–20×. Higher for young dependents.</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cg-acc">Accident cover multiplier (× income)</Label>
            <Input
              id="cg-acc"
              type="number"
              min="1"
              max="20"
              value={accidentMultiplier}
              onChange={(e) => setAccidentMultiplier(e.target.value)}
              data-testid="input-accident-multiplier"
            />
            <p className="text-xs text-muted">Industry norm: 3–7× annual income.</p>
          </div>
          <div
            className="col-span-full flex flex-col gap-1 rounded-md border border-gold/25 bg-gold/5 px-3 py-2 text-xs text-text/90"
            data-testid="health-recommendation-summary"
          >
            <div className="flex items-center gap-2 font-medium">
              <HeartPulse className="h-3.5 w-3.5 text-gold" />
              Health-cover recommendation
            </div>
            <div>
              <span className="text-muted">
                {selectedFamily?.label} in a {selectedTier?.label}, {ageInfo.label} →{" "}
              </span>
              <span className="font-semibold text-gold">
                {formatINRCompact(totals.health.recommended)}
              </span>
              <span className="text-muted"> (base range {selectedFamily?.rangeLabel})</span>
            </div>
            {selectedFamily?.note && (
              <p className="text-[11px] text-muted">{selectedFamily.note}</p>
            )}
          </div>
          <div className="col-span-full flex items-start gap-2 rounded-md border border-border bg-elevated/50 px-3 py-2 text-xs text-muted">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" />
            <span>
              Numbers are educational — actual cover needs depend on liabilities, dependants, city
              tier, and existing corpus. Discuss with a qualified advisor before acting.
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Coverage cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <CoverageCard
          icon={ShieldCheck}
          title="Life cover"
          description="Term life + LIC-style endowment policies combined."
          current={totals.life.current}
          recommended={totals.life.recommended}
          testIdSuffix="life"
        />
        <CoverageCard
          icon={Cross}
          title="Accident cover"
          description="Personal accident (PA) policies — pays a lump sum for permanent disability or death."
          current={totals.accident.current}
          recommended={totals.accident.recommended}
          testIdSuffix="accident"
        />
        <CoverageCard
          icon={HeartPulse}
          title="Health cover"
          description="Hospitalisation cover for you and your family (family floaters counted at the floater sum)."
          current={totals.health.current}
          recommended={totals.health.recommended}
          testIdSuffix="health"
        />
      </div>

      {policies.length === 0 && (
        <div className="mt-6 rounded-md border border-amber-400/40 bg-amber-400/5 p-4 text-sm text-amber-300">
          <p className="font-medium">You haven&apos;t added any policies yet.</p>
          <p className="mt-1 text-xs text-amber-300/80">
            The gap shown assumes zero cover. Add your policies on the Policies page for an accurate picture.
          </p>
          <Button asChild variant="outline" size="sm" className="mt-3">
            <Link to="/app/policies" data-testid="add-policies-cta">Add policies</Link>
          </Button>
        </div>
      )}

      <div className="mt-8">
        <Disclaimer />
      </div>
    </div>
  );
}

export default CoverageGapPage;
